<template>
  <SubscribeAccordionItem
    class="first"
    :isOpen="isOpen && !disabled"
    :disabled="disabled"
    @toggle="$emit('toggle')"
  >
    <template #head>
      <div>
        <div class="subscribe__step-label" v-if="!isDone && !disabled">{{ t('label') }}</div>
        <div class="subscribe__step-done" v-else-if="!disabled">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="34"
            height="34"
            viewBox="0 0 34 34"
            fill="none"
            role="img"
            :aria-label="t('stepDoneAlt')"
          >
            <rect width="34" height="34" rx="17" fill="#D7F3D2"/>
            <path d="M9 16.4L14.6 23L25 11" stroke="#04695D" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round"/>
          </svg>
        </div>

        <h3 class="subscribe__step-header h6">
          {{ t('header') }}
        </h3>

        <div class="subscribe__step-subheader" v-if="!disabled">
          {{ t('subheader') }}
        </div>

        <p class="subscribe__step-warning" v-if="warning" v-html="warning"></p>

        <button
          v-if="!disabled"
          type="button"
          class="subscribe__step-view-all"
          @click="$emit('view-all')"
        >
          <span>{{ t('buttonLabel') }}</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M4 12.0273C4 11.5707 4.37054 11.2002 4.82715 11.2002L17.96 11.2002L13.3535 6.3994C13.0374 6.06993 13.0485 5.5466 13.3779 5.23045C13.7074 4.91438 14.2307 4.92451 14.5469 5.25389L20.4707 11.4277C20.7773 11.7475 20.7773 12.2524 20.4707 12.5722L14.5469 18.7461C14.2307 19.0755 13.7074 19.0856 13.3779 18.7695C13.0485 18.4534 13.0374 17.93 13.3535 17.6006L17.9082 12.8535L4.82715 12.8535C4.37066 12.8535 4.0002 12.4838 4 12.0273Z" fill="#2A2C2C"/>
          </svg>
        </button>
      </div>
    </template>

    <template #toggler-content>
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="10" viewBox="0 0 20 10" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M19.6711 9.66434C20.1227 9.22954 20.1073 8.53882 19.6367 8.12159L10.8178 0.303836C10.3608 -0.101278 9.63923 -0.101278 9.18223 0.303836L0.363338 8.12159C-0.107325 8.53882 -0.122746 9.22954 0.328894 9.66434C0.780533 10.0991 1.52821 10.1134 1.99887 9.69616L10 2.60334L18.0011 9.69616C18.4718 10.1134 19.2195 10.0991 19.6711 9.66434Z" fill="#2A2C2C"/>
      </svg>
    </template>

    <div class="subscribe__step-products swiper" v-if="!disabled" ref="productsSwiperEl">
      <div class="swiper-wrapper">
        <div
          v-for="(product, idx) in limitedProducts"
          :key="productKey(product)"
          class="swiper-slide"
          :class="{ 'is-raised': raisedSlideIndex === idx }"
        >
          <ProductCard
            :product="product"
            :qty="getQty(product)"
            :subscriptionQty="getSubscriptionQty(product)"
            :canAddMore="canAddMore"
            :activeTipKey="activeTipKey"
            currencyLabel="грн"
            @add="$emit('add', product)"
            @openProductModal="$emit('openProductModal', product)"
            @cant-add="(tipKey) => onCantAdd(idx, tipKey)"
            @dismiss-tip="dismissTip"
          />
        </div>
      </div>

      <div class="subscribe__step-controls">
        <button type="button" class="subscribe__step-controls-prev" ref="productsPrevEl" :aria-label="swiperA11yMessages.prevSlideMessage">
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="20" viewBox="0 0 10 20" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M9.66434 0.328894C9.22954 -0.122746 8.53882 -0.107324 8.12159 0.363338L0.303836 9.18223C-0.101278 9.63923 -0.101278 10.3608 0.303836 10.8178L8.12159 19.6367C8.53882 20.1073 9.22954 20.1227 9.66434 19.6711C10.0991 19.2195 10.1134 18.4718 9.69616 18.0011L2.60334 10L9.69616 1.99887C10.1134 1.52821 10.0991 0.780533 9.66434 0.328894Z" fill="currentColor"/>
          </svg>
        </button>

        <div class="swiper-pagination" ref="productsPaginationEl"></div>

        <button type="button" class="subscribe__step-controls-next" ref="productsNextEl" :aria-label="swiperA11yMessages.nextSlideMessage">
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="20" viewBox="0 0 10 20" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M0.335658 0.328894C0.770464 -0.122746 1.46118 -0.107324 1.87841 0.363338L9.69616 9.18223C10.1013 9.63923 10.1013 10.3608 9.69616 10.8178L1.87841 19.6367C1.46118 20.1073 0.770464 20.1227 0.335658 19.6711C-0.099148 19.2195 -0.113395 18.4718 0.303837 18.0011L7.39666 10L0.303837 1.99887C-0.113395 1.52821 -0.099148 0.780533 0.335658 0.328894Z" fill="currentColor"/>
          </svg>
        </button>
      </div>
    </div>
  </SubscribeAccordionItem>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import Swiper from 'swiper'
import { A11y, Navigation, Pagination } from 'swiper/modules'

import SubscribeAccordionItem from './SubscribeAccordionItem.vue'
import ProductCard from './ProductCard.vue'

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  isDone: { type: Boolean, default: false },
  products: { type: Array, default: () => [] },
  productKey: { type: Function, required: true },
  getQty: { type: Function, required: true },
  getSubscriptionQty: { type: Function, default: () => 0 },
  canAddMore: { type: Boolean, default: true },
  warning: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
})

defineEmits(['toggle', 'add', 'openProductModal', 'view-all'])

const i18n = {
  uk: {
    label: '1 крок',
    header: 'Вибери блоки стіків',
    subheader: 'Обери стіки для пристрою glo™. У блоці - 10 пачок. Можна обрати від 2 до 5 блоків.',
    buttonLabel: 'переглянути всі',
    stepDoneAlt: 'Крок пройдено',
  },
  ru: {
    label: '1 шаг',
    header: 'Выбери блоки стиков',
    subheader: 'Выбери стики для устройства glo™. В блоке - 10 пачек. Можно выбрать от 2 до 5 блоков.',
    buttonLabel: 'просмотреть все',
    stepDoneAlt: 'Шаг пройден',
  },
}

const locale = computed(() => window.Shopify?.locale || 'uk')
const t = (key) => i18n[locale.value]?.[key] || key
const swiperA11yMessages = computed(() => {
  const isUk = locale.value === 'uk'

  return {
    enabled: true,
    slideLabelMessage: '',
    paginationBulletMessage: isUk ? 'Перейти до слайду {{index}}' : 'Перейти к слайду {{index}}',
    nextSlideMessage: isUk ? 'Наступний слайд' : 'Следующий слайд',
    prevSlideMessage: isUk ? 'Попередній слайд' : 'Предыдущий слайд',
  }
})

const productsSwiperEl = ref(null)
const productsPrevEl = ref(null)
const productsNextEl = ref(null)
const productsPaginationEl = ref(null)

let productsSwiperInstance = null

const limitedProducts = computed(() => props.products.slice(0, 9))

const raisedSlideIndex = ref(null)
const activeTipKey = ref(null)

let raisedT = null

const setSlideAccessibility = (slide, isVisible) => {
  if (isVisible) {
    slide.removeAttribute('inert')
    slide.removeAttribute('aria-hidden')
    return
  }

  slide.setAttribute('inert', '')
  slide.setAttribute('aria-hidden', 'true')
}

const updateProductsSlidesAccessibility = () => {
  if (!productsSwiperInstance) return

  const slides = Array.from(productsSwiperInstance.slides)
  const hasVisibleSlides = slides.some((slide) => (
    slide.classList.contains('swiper-slide-visible')
  ))
  const activeIndex = productsSwiperInstance.activeIndex || 0
  const perView = productsSwiperInstance.slidesPerViewDynamic?.() || 1
  const lastVisibleFallbackIndex = activeIndex + perView - 1

  slides.forEach((slide, index) => {
    const isVisible = hasVisibleSlides
      ? slide.classList.contains('swiper-slide-visible')
      : index >= activeIndex && index <= lastVisibleFallbackIndex

    setSlideAccessibility(slide, isVisible)
  })
}

const resetProductsSlidesAccessibility = (root) => {
  root?.querySelectorAll('.swiper-slide').forEach((slide) => {
    slide.removeAttribute('inert')
    slide.removeAttribute('aria-hidden')
  })
}

const destroyProductsSwiper = () => {
  if (!productsSwiperInstance) return

  const el = productsSwiperInstance.el
  productsSwiperInstance.destroy(true, true)
  productsSwiperInstance = null

  if (el) {
    const wrapper = el.querySelector('.swiper-wrapper')
    if (wrapper) wrapper.removeAttribute('style')
    el.querySelectorAll('.swiper-slide').forEach((slide) => slide.removeAttribute('style'))
    resetProductsSlidesAccessibility(el)
  }
}

const initProductsSwiper = () => {
  if (productsSwiperInstance || !productsSwiperEl.value) return

  productsSwiperInstance = new Swiper(productsSwiperEl.value, {
    modules: [A11y, Navigation, Pagination],
    slidesPerView: 'auto',
    spaceBetween: 16,
    watchSlidesProgress: true,
    pagination: {
      el: productsPaginationEl.value,
      clickable: true,
    },
    navigation: {
      prevEl: productsPrevEl.value,
      nextEl: productsNextEl.value,
    },
    a11y: swiperA11yMessages.value,
    breakpoints: {
      1024: { slidesPerView: 'auto' },
    },
    on: {
      init: updateProductsSlidesAccessibility,
      slideChange: updateProductsSlidesAccessibility,
      resize: updateProductsSlidesAccessibility,
      update: updateProductsSlidesAccessibility,
      transitionEnd: updateProductsSlidesAccessibility,
    },
  })

  updateProductsSlidesAccessibility()
}

const updateProductsSwiper = () => {
  if (!productsSwiperInstance) return
  productsSwiperInstance.update()
  productsSwiperInstance.updateSlides()
  productsSwiperInstance.updateProgress()
  productsSwiperInstance.updateSize()
  updateProductsSlidesAccessibility()
}

const onCantAdd = (idx, tipKey) => {
  raisedSlideIndex.value = idx
  clearTimeout(raisedT)
  raisedT = setTimeout(() => {
    raisedSlideIndex.value = null
  }, 2000)

  activeTipKey.value = tipKey
}

const dismissTip = (tipKey) => {
  if (!tipKey || activeTipKey.value === tipKey) activeTipKey.value = null
}

watch(
  () => [props.isOpen, props.products.length],
  async ([open]) => {
    if (open) {
      await nextTick()
      initProductsSwiper()
      await nextTick()
      updateProductsSwiper()
    } else {
      destroyProductsSwiper()
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  clearTimeout(raisedT)
  destroyProductsSwiper()
})
</script>
